import NetInfo from '@react-native-community/netinfo';
import type { ImagePickerAsset } from 'expo-image-picker';
import * as FileSystem from 'expo-file-system/legacy';

import type { GiphyGif } from '@/src/features/giphy/giphy-service';
import {
  createPost,
  createPostComment,
  deletePostComment,
  emptyCommentReactionCounts,
  emptyReactionCounts,
  setCommentReaction,
  setPostReaction,
  type Reaction,
  type CommentUploadMediaKind,
  type ProfileMention,
  type PostVisibility,
  type PostComment,
  type PostMediaKind,
  type PublicPost,
} from '@/src/features/posts/post-service';
import {
  setFollow,
  updateUserProfile,
  uploadProfileImage,
  type ProfileImageKind,
  type UpdateProfileInput,
} from '@/src/features/profile/profile-service';
import { persistentStorage } from '@/src/lib/persistent-storage';

type SyncStatus = 'pending' | 'uploading' | 'failed';

type QueuedMediaAsset = {
  uri: string;
  fileName: string | null;
  fileSize: number | null;
  width: number | null;
  height: number | null;
  duration: number | null;
  mimeType: string | null;
  type: 'image' | 'video' | null;
};

type QueuedOperationBase = {
  id: string;
  attempts: number;
  createdAtMillis: number;
  lastError: string | null;
  status: SyncStatus;
  uid: string;
};

export type QueuedCreatePostOperation = QueuedOperationBase & {
  type: 'create_post';
  payload: {
    authorFullName: string | null;
    body: string;
    linkPreviewSourceUrl: string | null;
    profileMentions: ProfileMention[];
    visibility: PostVisibility;
    mediaAsset: QueuedMediaAsset | null;
    mediaAssets?: QueuedMediaAsset[];
    mediaKind: PostMediaKind | null;
  };
};

export type QueuedCreateCommentOperation = QueuedOperationBase & {
  type: 'create_comment';
  payload: {
    body: string;
    giphyGif: GiphyGif | null;
    mediaAsset: QueuedMediaAsset | null;
    mediaKind: CommentUploadMediaKind | null;
    parentCommentId: string | null;
    replyToCommentId: string | null;
    postId: string;
    profileMentions: ProfileMention[];
  };
};

export type QueuedDeleteCommentOperation = QueuedOperationBase & {
  type: 'delete_comment';
  payload: {
    commentId: string;
    postId: string;
  };
};

export type QueuedSetPostLikeOperation = QueuedOperationBase & {
  type: 'set_post_like';
  payload: {
    liked: boolean;
    post: PublicPost;
  };
};

export type QueuedSetPostReactionOperation = QueuedOperationBase & {
  type: 'set_post_reaction';
  payload: {
    mutationId?: string;
    post: PublicPost;
    reaction: Reaction | null;
  };
};

export type QueuedSetCommentReactionOperation = QueuedOperationBase & {
  type: 'set_comment_reaction';
  payload: {
    commentId: string;
    mutationId?: string;
    postId: string;
    reaction: Reaction | null;
  };
};

export type QueuedSetFollowingOperation = QueuedOperationBase & {
  type: 'set_following';
  payload: {
    following: boolean;
    targetUid: string;
  };
};

export type QueuedUpdateProfileOperation = QueuedOperationBase & {
  type: 'update_profile';
  payload: {
    imageAsset: QueuedMediaAsset | null;
    imageKind: ProfileImageKind | null;
    input: UpdateProfileInput;
  };
};

export type QueuedOutboxOperation =
  | QueuedCreatePostOperation
  | QueuedCreateCommentOperation
  | QueuedDeleteCommentOperation
  | QueuedSetPostLikeOperation
  | QueuedSetPostReactionOperation
  | QueuedSetCommentReactionOperation
  | QueuedSetFollowingOperation
  | QueuedUpdateProfileOperation;

type OutboxListener = (operations: QueuedOutboxOperation[]) => void;

const outboxStorageKey = 'frienzzi-offline-outbox-v1';
const listeners = new Set<OutboxListener>();
let operationsCache: QueuedOutboxOperation[] | null = null;
let processing = false;

function makeId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function cloneOperations(operations: QueuedOutboxOperation[]) {
  return operations.map((operation) => ({ ...operation }));
}

async function loadOperations() {
  if (operationsCache) {
    return operationsCache;
  }

  const rawValue = await persistentStorage.getItem(outboxStorageKey);
  operationsCache = rawValue
    ? (JSON.parse(rawValue) as QueuedOutboxOperation[])
    : [];

  return operationsCache;
}

async function saveOperations(nextOperations: QueuedOutboxOperation[]) {
  operationsCache = nextOperations;
  await persistentStorage.setItem(
    outboxStorageKey,
    JSON.stringify(nextOperations),
  );

  const snapshot = cloneOperations(nextOperations);
  listeners.forEach((listener) => listener(snapshot));
}

function operationCoalesceKey(operation: QueuedOutboxOperation) {
  switch (operation.type) {
    case 'set_post_like':
      return `${operation.type}:${operation.uid}:${operation.payload.post.id}`;
    case 'set_post_reaction':
      return `${operation.type}:${operation.uid}:${operation.payload.post.id}`;
    case 'set_comment_reaction':
      return `${operation.type}:${operation.uid}:${operation.payload.postId}:${operation.payload.commentId}`;
    case 'set_following':
      return `${operation.type}:${operation.uid}:${operation.payload.targetUid}`;
    case 'update_profile':
      return `${operation.type}:${operation.uid}:${operation.payload.imageKind ?? 'fields'}`;
    default:
      return null;
  }
}

function queuedAssetToImagePickerAsset(
  asset: QueuedMediaAsset,
): ImagePickerAsset {
  return {
    uri: asset.uri,
    fileName: asset.fileName ?? undefined,
    fileSize: asset.fileSize ?? undefined,
    mimeType: asset.mimeType ?? undefined,
    type: asset.type ?? undefined,
    width: asset.width ?? 0,
    height: asset.height ?? 0,
    duration: asset.duration ?? undefined,
  };
}

function safeFileName(asset: ImagePickerAsset) {
  const fallback =
    asset.type === 'video'
      ? 'queued-video.mp4'
      : asset.type === 'image'
        ? 'queued-image.jpg'
        : 'queued-media';

  return (asset.fileName ?? fallback).replace(/[^a-zA-Z0-9._-]/g, '_');
}

function normalizeAssetFileSize(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.round(value)
    : null;
}

function getPickerAssetFileSize(asset: ImagePickerAsset) {
  return (
    normalizeAssetFileSize(asset.fileSize) ??
    normalizeAssetFileSize(asset.file?.size)
  );
}

async function getLocalFileSize(uri: string) {
  if (!uri.startsWith('file://')) {
    return null;
  }

  try {
    const info = await FileSystem.getInfoAsync(uri);

    return info.exists && 'size' in info
      ? normalizeAssetFileSize(info.size)
      : null;
  } catch {
    return null;
  }
}

async function persistMediaAsset(
  asset: ImagePickerAsset | null | undefined,
  operationId: string,
): Promise<QueuedMediaAsset | null> {
  if (!asset) {
    return null;
  }

  let uri = asset.uri;
  let fileSize = getPickerAssetFileSize(asset);

  try {
    const directory = `${FileSystem.documentDirectory ?? ''}frienzzi-outbox/`;

    if (directory.startsWith('file://')) {
      await FileSystem.makeDirectoryAsync(directory, { intermediates: true });

      const destination = `${directory}${operationId}-${safeFileName(asset)}`;
      await FileSystem.copyAsync({ from: asset.uri, to: destination });
      uri = destination;
      fileSize = (await getLocalFileSize(destination)) ?? fileSize;
    }
  } catch {
    uri = asset.uri;
  }

  fileSize = fileSize ?? (await getLocalFileSize(uri));

  return {
    uri,
    fileName: asset.fileName ?? null,
    fileSize,
    width: asset.width ?? null,
    height: asset.height ?? null,
    duration: asset.duration ?? null,
    mimeType: asset.mimeType ?? null,
    type: asset.type === 'video' ? 'video' : 'image',
  };
}

async function persistMediaAssets(
  assets: ImagePickerAsset[] | null | undefined,
  operationId: string,
) {
  if (!assets || assets.length === 0) {
    return [];
  }

  const queuedAssets: QueuedMediaAsset[] = [];

  for (const [index, asset] of assets.entries()) {
    const queuedAsset = await persistMediaAsset(asset, `${operationId}-${index}`);

    if (queuedAsset) {
      queuedAssets.push(queuedAsset);
    }
  }

  return queuedAssets;
}

async function deleteQueuedMedia(asset: QueuedMediaAsset | null) {
  if (!asset?.uri.startsWith('file://')) {
    return;
  }

  try {
    await FileSystem.deleteAsync(asset.uri, { idempotent: true });
  } catch {
    // The queue should still clear if temporary media cleanup fails.
  }
}

async function deleteQueuedMediaAssets(assets: QueuedMediaAsset[] | undefined) {
  for (const asset of assets ?? []) {
    await deleteQueuedMedia(asset);
  }
}

function messageFromError(error: unknown) {
  return error instanceof Error ? error.message : String(error);
}

export function isRecoverableNetworkError(error: unknown) {
  const message = messageFromError(error).toLowerCase();
  const code =
    typeof error === 'object' && error && 'code' in error
      ? String((error as { code?: unknown }).code).toLowerCase()
      : '';

  return (
    code.includes('unavailable') ||
    code.includes('deadline-exceeded') ||
    code.includes('network') ||
    code === 'storage/unknown' ||
    message.includes('network') ||
    message.includes('offline') ||
    message.includes('failed to fetch') ||
    message.includes('firebase storage: an unknown error occurred') ||
    message.includes('timed out')
  );
}

export async function isNetworkAvailable() {
  const state = await NetInfo.fetch();
  return state.isConnected === true && state.isInternetReachable !== false;
}

export async function getOutboxOperations() {
  return cloneOperations(await loadOperations());
}

export function subscribeToOutbox(listener: OutboxListener) {
  listeners.add(listener);

  void loadOperations().then((operations) =>
    listener(cloneOperations(operations)),
  );

  return () => {
    listeners.delete(listener);
  };
}

async function enqueueOperation(operation: QueuedOutboxOperation) {
  const operations = await loadOperations();
  const coalesceKey = operationCoalesceKey(operation);
  const nextOperations = coalesceKey
    ? operations.filter(
        (currentOperation) =>
          operationCoalesceKey(currentOperation) !== coalesceKey,
      )
    : operations;

  nextOperations.push(operation);
  await saveOperations(nextOperations);
}

async function updateOperation(
  id: string,
  update: Partial<QueuedOutboxOperation>,
) {
  const operations = await loadOperations();
  await saveOperations(
    operations.map((operation) =>
      operation.id === id
        ? ({ ...operation, ...update } as QueuedOutboxOperation)
        : operation,
    ),
  );
}

async function removeOperation(id: string) {
  const operations = await loadOperations();
  const operation = operations.find((candidate) => candidate.id === id);

  await saveOperations(operations.filter((candidate) => candidate.id !== id));

  if (operation?.type === 'create_post') {
    await deleteQueuedMedia(operation.payload.mediaAsset);
    await deleteQueuedMediaAssets(operation.payload.mediaAssets);
  }

  if (operation?.type === 'create_comment') {
    await deleteQueuedMedia(operation.payload.mediaAsset);
  }

  if (operation?.type === 'update_profile') {
    await deleteQueuedMedia(operation.payload.imageAsset);
  }
}

export async function discardQueuedOperation(id: string) {
  const operations = await loadOperations();
  const operation = operations.find((candidate) => candidate.id === id);

  if (!operation) {
    return false;
  }

  await removeOperation(id);

  return true;
}

function buildBaseOperation(uid: string): QueuedOperationBase {
  return {
    id: makeId(),
    attempts: 0,
    createdAtMillis: Date.now(),
    lastError: null,
    status: 'pending',
    uid,
  };
}

export async function queueCreatePost({
  authorFullName,
  authorUid,
  body,
  mediaAsset,
  mediaAssets,
  mediaKind,
  linkPreviewSourceUrl = null,
  profileMentions = [],
  visibility = 'public',
}: {
  authorFullName: string | null;
  authorUid: string;
  body: string;
  linkPreviewSourceUrl?: string | null;
  profileMentions?: ProfileMention[];
  visibility?: PostVisibility;
  mediaAsset?: ImagePickerAsset | null;
  mediaAssets?: ImagePickerAsset[];
  mediaKind?: PostMediaKind | null;
}) {
  const base = buildBaseOperation(authorUid);
  const queuedMediaAssets = await persistMediaAssets(mediaAssets, base.id);

  await enqueueOperation({
    ...base,
    type: 'create_post',
    payload: {
      authorFullName,
      body,
      linkPreviewSourceUrl,
      profileMentions,
      visibility,
      mediaAsset:
        queuedMediaAssets.length > 0
          ? null
          : await persistMediaAsset(mediaAsset, base.id),
      mediaAssets: queuedMediaAssets,
      mediaKind: mediaKind ?? null,
    },
  });
}

export async function queueCreateComment({
  authorUid,
  body,
  giphyGif,
  mediaAsset,
  mediaKind,
  parentCommentId = null,
  replyToCommentId = null,
  postId,
  profileMentions = [],
}: {
  authorUid: string;
  body: string;
  giphyGif?: GiphyGif | null;
  mediaAsset?: ImagePickerAsset | null;
  mediaKind?: CommentUploadMediaKind | null;
  parentCommentId?: string | null;
  replyToCommentId?: string | null;
  postId: string;
  profileMentions?: ProfileMention[];
}) {
  const base = buildBaseOperation(authorUid);

  await enqueueOperation({
    ...base,
    type: 'create_comment',
    payload: {
      body,
      giphyGif: giphyGif ?? null,
      mediaAsset: await persistMediaAsset(mediaAsset, base.id),
      mediaKind: mediaKind ?? null,
      parentCommentId,
      replyToCommentId,
      postId,
      profileMentions,
    },
  });
}

export async function queueDeleteComment({
  commentId,
  postId,
  uid,
}: {
  commentId: string;
  postId: string;
  uid: string;
}) {
  await enqueueOperation({
    ...buildBaseOperation(uid),
    type: 'delete_comment',
    payload: { commentId, postId },
  });
}

export async function queueSetPostLike({
  liked,
  post,
  uid,
}: {
  liked: boolean;
  post: PublicPost;
  uid: string;
}) {
  await enqueueOperation({
    ...buildBaseOperation(uid),
    type: 'set_post_like',
    payload: { liked, post },
  });
}

export async function queueSetPostReaction({
  mutationId,
  post,
  reaction,
  uid,
}: {
  mutationId?: string;
  post: PublicPost;
  reaction: Reaction | null;
  uid: string;
}) {
  await enqueueOperation({
    ...buildBaseOperation(uid),
    type: 'set_post_reaction',
    payload: { ...(mutationId ? { mutationId } : {}), post, reaction },
  });
}

export async function queueSetCommentReaction({
  commentId,
  mutationId,
  postId,
  reaction,
  uid,
}: {
  commentId: string;
  mutationId?: string;
  postId: string;
  reaction: Reaction | null;
  uid: string;
}) {
  await enqueueOperation({
    ...buildBaseOperation(uid),
    type: 'set_comment_reaction',
    payload: {
      commentId,
      ...(mutationId ? { mutationId } : {}),
      postId,
      reaction,
    },
  });
}

export async function queueSetFollowing({
  currentUid,
  following,
  targetUid,
}: {
  currentUid: string;
  following: boolean;
  targetUid: string;
}) {
  await enqueueOperation({
    ...buildBaseOperation(currentUid),
    type: 'set_following',
    payload: { following, targetUid },
  });
}

export async function queueUpdateProfile({
  imageAsset,
  imageKind,
  input,
  uid,
}: {
  imageAsset?: ImagePickerAsset | null;
  imageKind?: ProfileImageKind | null;
  input: UpdateProfileInput;
  uid: string;
}) {
  const base = buildBaseOperation(uid);

  await enqueueOperation({
    ...base,
    type: 'update_profile',
    payload: {
      imageAsset: await persistMediaAsset(imageAsset, base.id),
      imageKind: imageKind ?? null,
      input,
    },
  });
}

async function processOperation(operation: QueuedOutboxOperation) {
  switch (operation.type) {
    case 'create_post':
      await createPost({
        authorFullName: operation.payload.authorFullName,
        authorUid: operation.uid,
        body: operation.payload.body,
        linkPreviewSourceUrl: operation.payload.linkPreviewSourceUrl ?? null,
        profileMentions: operation.payload.profileMentions ?? [],
        visibility: operation.payload.visibility,
        mediaAsset: operation.payload.mediaAsset
          ? queuedAssetToImagePickerAsset(operation.payload.mediaAsset)
          : null,
        ...(operation.payload.mediaAssets?.length
          ? {
              mediaAssets: operation.payload.mediaAssets.map(
                queuedAssetToImagePickerAsset,
              ),
            }
          : {}),
        mediaKind: operation.payload.mediaKind,
      });
      return;
    case 'create_comment':
      await createPostComment({
        authorUid: operation.uid,
        body: operation.payload.body,
        giphyGif: operation.payload.giphyGif,
        mediaAsset: operation.payload.mediaAsset
          ? queuedAssetToImagePickerAsset(operation.payload.mediaAsset)
          : null,
        mediaKind: operation.payload.mediaKind,
        parentCommentId: operation.payload.parentCommentId ?? null,
        replyToCommentId: operation.payload.replyToCommentId ?? null,
        postId: operation.payload.postId,
        profileMentions: operation.payload.profileMentions ?? [],
      });
      return;
    case 'delete_comment':
      await deletePostComment({
        commentId: operation.payload.commentId,
        postId: operation.payload.postId,
        uid: operation.uid,
      });
      return;
    case 'set_post_like':
      await setPostReaction({
        post: operation.payload.post,
        reaction: operation.payload.liked ? 'like' : null,
        uid: operation.uid,
      });
      return;
    case 'set_post_reaction':
      await setPostReaction({
        mutationId: operation.payload.mutationId,
        post: operation.payload.post,
        reaction: operation.payload.reaction,
        uid: operation.uid,
      });
      return;
    case 'set_comment_reaction':
      await setCommentReaction({
        commentId: operation.payload.commentId,
        mutationId: operation.payload.mutationId,
        postId: operation.payload.postId,
        reaction: operation.payload.reaction,
        uid: operation.uid,
      });
      return;
    case 'set_following':
      await setFollow({
        currentUid: operation.uid,
        following: operation.payload.following,
        targetUid: operation.payload.targetUid,
      });
      return;
    case 'update_profile': {
      let imageUpdates: UpdateProfileInput = {};

      if (operation.payload.imageAsset && operation.payload.imageKind) {
        const uploadResult = await uploadProfileImage({
          asset: queuedAssetToImagePickerAsset(operation.payload.imageAsset),
          kind: operation.payload.imageKind,
          uid: operation.uid,
        });

        imageUpdates =
          operation.payload.imageKind === 'avatar'
            ? {
                photoURL: uploadResult.downloadURL,
                profileImages: { avatar: uploadResult.profileImage },
              }
            : {
                coverPhotoURL: uploadResult.downloadURL,
                profileImages: { cover: uploadResult.profileImage },
              };
      }

      await updateUserProfile(operation.uid, {
        ...operation.payload.input,
        ...imageUpdates,
      });
    }
  }
}

export async function processOutbox() {
  if (processing || !(await isNetworkAvailable())) {
    return;
  }

  processing = true;

  try {
    const operations = await getOutboxOperations();

    for (const operation of operations) {
      await updateOperation(operation.id, {
        attempts: operation.attempts + 1,
        lastError: null,
        status: 'uploading',
      } as Partial<QueuedOutboxOperation>);

      try {
        await processOperation(operation);
        await removeOperation(operation.id);
      } catch (error) {
        await updateOperation(operation.id, {
          attempts: operation.attempts + 1,
          lastError: messageFromError(error),
          status: 'failed',
        } as Partial<QueuedOutboxOperation>);
      }
    }
  } finally {
    processing = false;
  }
}

export function queuedCreatePostToPublicPost(
  operation: QueuedCreatePostOperation,
): PublicPost {
  const mediaKind = operation.payload.mediaKind;
  const mediaAsset = operation.payload.mediaAsset;
  const mediaAssets =
    mediaKind === 'image' && operation.payload.mediaAssets?.length
      ? operation.payload.mediaAssets
      : mediaKind === 'image' && mediaAsset
        ? [mediaAsset]
        : [];
  const mediaItems = mediaAssets.map((asset, index) => ({
    id: `photo-${index + 1}`,
    order: index,
    kind: 'image' as const,
    storagePath: `local/${operation.id}/${index}`,
    downloadURL: asset.uri,
    contentType: asset.mimeType ?? 'image/jpeg',
    sizeBytes: asset.fileSize ?? 0,
    width: asset.width ?? 0,
    height: asset.height ?? 0,
    durationMillis: null,
    thumbnail: null,
    thumbnailStatus: 'queued' as const,
  }));
  const singleMedia =
    mediaKind && mediaAsset
      ? {
          kind: mediaKind,
          storagePath: `local/${operation.id}`,
          downloadURL: mediaAsset.uri,
          contentType:
            mediaAsset.mimeType ??
            (mediaKind === 'image' ? 'image/jpeg' : 'video/mp4'),
          sizeBytes: mediaAsset.fileSize ?? 0,
          width: mediaAsset.width ?? 0,
          height: mediaAsset.height ?? 0,
          durationMillis:
            mediaKind === 'video' ? (mediaAsset.duration ?? null) : null,
          thumbnail: null,
          thumbnailStatus: 'queued' as const,
        }
      : null;

  return {
    id: operation.id,
    authorUid: operation.uid,
    authorFullName: operation.payload.authorFullName,
    authorUsername: null,
    authorPhotoURL: null,
    body: operation.payload.body.trim() || null,
    visibility: operation.payload.visibility ?? 'public',
    media: mediaItems[0] ?? singleMedia,
    mediaItems,
    likeCount: 0,
    reactionCounts: emptyReactionCounts(),
    reactionCount: 0,
    commentCount: 0,
    shareCount: 0,
    profileMentions: operation.payload.profileMentions ?? [],
    linkCard: null,
    linkPreview: null,
    reshare: null,
    feedStats: {
      commentOpenCount: 0,
      detailOpenCount: 0,
      dwellBucketTotal: 0,
      impressionCount: 0,
    },
    createdAt: new Date(operation.createdAtMillis),
    editCount: 0,
    editedAt: null,
    updatedAt: new Date(operation.createdAtMillis),
    localCreatedAtMillis: operation.createdAtMillis,
    syncStatus: operation.status,
  };
}

export function queuedCreateCommentToComment(
  operation: QueuedCreateCommentOperation,
): PostComment {
  const mediaKind = operation.payload.mediaKind;
  const mediaAsset = operation.payload.mediaAsset;
  const giphyGif = operation.payload.giphyGif as {
    giphyId?: string;
    height?: number;
    originalURL?: string;
    pageURL?: string;
    previewURL?: string;
    title?: string;
    width?: number;
  } | null;

  return {
    id: operation.id,
    authorUid: operation.uid,
    authorFullName: null,
    authorUsername: null,
    authorPhotoURL: null,
    body: operation.payload.body.trim() || null,
    parentCommentId: operation.payload.parentCommentId ?? null,
    profileMentions: operation.payload.profileMentions ?? [],
    linkPreview: null,
    reactionCounts: emptyCommentReactionCounts(),
    reactionCount: 0,
    replyToCommentId: operation.payload.replyToCommentId ?? null,
    replyToFullName: null,
    replyToUid: null,
    replyToUsername: null,
    media:
      mediaKind && mediaAsset
        ? {
            kind: mediaKind,
            source: 'upload',
            storagePath: `local/${operation.id}`,
            downloadURL: mediaAsset.uri,
            contentType:
              mediaAsset.mimeType ??
              (mediaKind === 'image' ? 'image/jpeg' : 'video/mp4'),
            sizeBytes: mediaAsset.fileSize ?? 0,
            width: mediaAsset.width ?? 0,
            height: mediaAsset.height ?? 0,
            durationMillis:
              mediaKind === 'video' ? (mediaAsset.duration ?? null) : null,
            thumbnail: null,
            thumbnailStatus: 'queued',
          }
        : giphyGif
          ? {
              kind: 'gif',
              source: 'giphy',
              giphyId: giphyGif.giphyId ?? '',
              title: giphyGif.title ?? 'GIPHY GIF',
              pageURL: giphyGif.pageURL ?? '',
              previewURL: giphyGif.previewURL ?? '',
              originalURL: giphyGif.originalURL ?? '',
              width: giphyGif.width ?? 0,
              height: giphyGif.height ?? 0,
            }
          : null,
    createdAt: new Date(operation.createdAtMillis),
    editCount: 0,
    editedAt: null,
    updatedAt: new Date(operation.createdAtMillis),
    localCreatedAtMillis: operation.createdAtMillis,
    syncStatus: operation.status,
  };
}
